import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import {
  getCuentasContables, crearCuentaContable, actualizarCuentaContable, borrarCuentaContable,
  aprobarCuentaContable, aprobarCuentasContablesMasivo,
  TIPOS_CUENTA, tipoLabel, LITERALES_TRIBUTARIOS,
} from '../../lib/contabilidadApi'

const COLOR_TIPO = {
  activo:     { bg: '#dbeafe', fg: '#1e3a8a' },
  pasivo:     { bg: '#fee2e2', fg: '#991b1b' },
  patrimonio: { bg: '#fef3c7', fg: '#92400e' },
  ingreso:    { bg: '#d1fae5', fg: '#064e3b' },
  egreso:     { bg: '#ede9fe', fg: '#4c1d95' },
}

const MONEDA_LABEL = { 0: '—', 1: 'UYU', 2: 'USD' }

export default function PlanCuentas() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroMoneda, setFiltroMoneda] = useState('')           // '' | '0' | '1' | '2'
  const [filtroImputable, setFiltroImputable] = useState('')     // '' | 'si' | 'no'
  const [filtroLiteral, setFiltroLiteral] = useState('')         // '' | 'con' | 'sin'
  const [filtroDifCambio, setFiltroDifCambio] = useState('')     // '' | 'S' | 'N'
  const [filtroEstado, setFiltroEstado] = useState('')           // '' | 'borrador' | 'validado_roberto' | 'rechazado'
  const [busqueda, setBusqueda] = useState('')
  const [expandidos, setExpandidos] = useState(() => new Set())
  const [modal, setModal] = useState(null)
  const [aprobandoMasivo, setAprobandoMasivo] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    setItems(await getCuentasContables())
    setLoading(false)
  }

  const filtrados = useMemo(() => {
    let f = items
    if (filtroTipo) f = f.filter(c => c.tipo === filtroTipo)
    if (filtroMoneda !== '') f = f.filter(c => String(c.cod_moneda ?? 0) === filtroMoneda)
    if (filtroImputable === 'si') f = f.filter(c => c.es_imputable)
    if (filtroImputable === 'no') f = f.filter(c => !c.es_imputable)
    if (filtroLiteral === 'con') f = f.filter(c => (c.literal_trib || 0) > 0)
    if (filtroLiteral === 'sin') f = f.filter(c => !(c.literal_trib || 0))
    if (filtroDifCambio) f = f.filter(c => c.dif_cambio === filtroDifCambio)
    if (filtroEstado) f = f.filter(c => c.estado_validacion === filtroEstado)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      f = f.filter(c =>
        (c.codigo || '').toLowerCase().includes(q) ||
        (c.nombre || '').toLowerCase().includes(q)
      )
    }
    return f
  }, [items, filtroTipo, filtroMoneda, filtroImputable, filtroLiteral, filtroDifCambio, filtroEstado, busqueda])

  const stats = useMemo(() => {
    let total = 0, imputables = 0, borrador = 0, validadas = 0
    for (const c of items) {
      total++
      if (c.es_imputable) imputables++
      if (c.estado_validacion === 'borrador') borrador++
      else if (c.estado_validacion === 'validado_roberto') validadas++
    }
    return { total, imputables, borrador, validadas }
  }, [items])

  const hayFiltro = busqueda.trim() || filtroTipo || filtroMoneda !== '' || filtroImputable || filtroLiteral || filtroDifCambio || filtroEstado

  function toggleExpand(codigo) {
    setExpandidos(prev => {
      const next = new Set(prev)
      if (next.has(codigo)) next.delete(codigo)
      else next.add(codigo)
      return next
    })
  }
  function expandirTodo() { setExpandidos(new Set(items.filter(c => c.nivel < 5).map(c => c.codigo))) }
  function colapsarTodo() { setExpandidos(new Set()) }
  function limpiarFiltros() {
    setBusqueda(''); setFiltroTipo(''); setFiltroMoneda(''); setFiltroImputable('')
    setFiltroLiteral(''); setFiltroDifCambio(''); setFiltroEstado('')
  }

  async function guardar(form) {
    try {
      if (form.id) await actualizarCuentaContable(form.id, form)
      else await crearCuentaContable(form)
      setModal(null)
      load()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function borrar(item) {
    if (!confirm(`¿Borrar la cuenta "${item.codigo} ${item.nombre}"? Si hay hijas también deberás borrarlas.`)) return
    try {
      await borrarCuentaContable(item.id)
      load()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function aprobarUna(item) {
    try {
      await aprobarCuentaContable(item.id, 'validado_roberto')
      load()
    } catch (e) { alert('Error aprobando: ' + e.message) }
  }

  async function aprobarVisiblesBorradores() {
    const ids = filtrados.filter(c => c.estado_validacion === 'borrador').map(c => c.id)
    if (!ids.length) return alert('No hay cuentas en borrador en la vista actual.')
    if (!confirm(`Aprobar ${ids.length} cuenta${ids.length === 1 ? '' : 's'} en borrador?`)) return
    setAprobandoMasivo(true)
    try {
      const n = await aprobarCuentasContablesMasivo(ids)
      alert(`${n} cuentas aprobadas.`)
      load()
    } catch (e) { alert('Error: ' + e.message) }
    finally { setAprobandoMasivo(false) }
  }

  // Filtrar visibles según expansión.
  // Regla simple: nivel 1-2 siempre se ve · resto se ve solo si su padre directo está expandido.
  // (Antes requería que TODOS los ancestros estuvieran expandidos → si abrías directo un nodo
  //  intermedio, sus hijos no aparecían. Ahora: expandís un nodo → ves sus hijos.)
  const visibles = useMemo(() => {
    if (hayFiltro) return filtrados
    return filtrados.filter(c => {
      if (c.nivel <= 2) return true
      return c.parent_codigo && expandidos.has(c.parent_codigo)
    })
  }, [filtrados, expandidos, hayFiltro])

  return (
    <AdminLayout>
      <div className="pc-wrap">
        <div className="pc-headline">
          <div>
            <h1>Plan de cuentas Zeta</h1>
            <p>363 cuentas oficiales Zeta. Roberto aprueba (estado <code>borrador</code> → <code>validado</code>).</p>
          </div>
          <button className="pc-btn-primary" onClick={() => setModal({})}>+ Nueva cuenta</button>
        </div>

        {/* KPIs */}
        <div className="pc-kpis">
          <Kpi label="Total" val={stats.total} color="#1A3461" />
          <Kpi label="Imputables" val={stats.imputables} color="#0d9488" />
          <Kpi label="Validadas" val={stats.validadas} color="#16a34a" />
          <Kpi label="En borrador" val={stats.borrador} color={stats.borrador ? '#d97706' : '#94a3b8'} />
          {TIPOS_CUENTA.map(t => {
            const n = items.filter(i => i.tipo === t.value).length
            return <Kpi key={t.value} label={t.label} val={n} color={COLOR_TIPO[t.value]?.fg} />
          })}
        </div>

        {/* Filtros */}
        <div className="pc-toolbar">
          <input
            type="search"
            placeholder="Buscar por código o nombre…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
            <option value="">Todos los tipos</option>
            {TIPOS_CUENTA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={filtroMoneda} onChange={(e) => setFiltroMoneda(e.target.value)}>
            <option value="">Toda moneda</option>
            <option value="0">Grupo (no movible)</option>
            <option value="1">UYU</option>
            <option value="2">USD</option>
          </select>
          <select value={filtroImputable} onChange={(e) => setFiltroImputable(e.target.value)}>
            <option value="">Imputable (sí/no)</option>
            <option value="si">Solo imputables</option>
            <option value="no">No imputables</option>
          </select>
          <select value={filtroLiteral} onChange={(e) => setFiltroLiteral(e.target.value)}>
            <option value="">Lit. trib.</option>
            <option value="con">Con literal</option>
            <option value="sin">Sin literal</option>
          </select>
          <select value={filtroDifCambio} onChange={(e) => setFiltroDifCambio(e.target.value)}>
            <option value="">Dif. cambio</option>
            <option value="S">S — genera</option>
            <option value="N">N — no</option>
          </select>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
            <option value="">Cualquier estado</option>
            <option value="borrador">⏳ Borrador</option>
            <option value="validado_roberto">✓ Validadas</option>
            <option value="rechazado">✗ Rechazadas</option>
          </select>
          <button className="pc-btn-ghost" onClick={expandirTodo}>Expandir todo</button>
          <button className="pc-btn-ghost" onClick={colapsarTodo}>Colapsar</button>
          {hayFiltro && <button className="pc-btn-ghost" onClick={limpiarFiltros}>Limpiar filtros</button>}
          <div className="pc-toolbar-info">{filtrados.length} cuentas</div>
        </div>

        {/* Aprobación masiva */}
        {hayFiltro && filtrados.some(c => c.estado_validacion === 'borrador') && (
          <div className="pc-banner-aprobacion">
            Hay <strong>{filtrados.filter(c => c.estado_validacion === 'borrador').length}</strong> cuenta(s) en borrador en esta vista.
            <button className="pc-btn-primary" disabled={aprobandoMasivo} onClick={aprobarVisiblesBorradores}>
              {aprobandoMasivo ? 'Aprobando…' : 'Aprobar todas las visibles'}
            </button>
          </div>
        )}

        {/* Tabla */}
        {loading ? (
          <div className="pc-loading">Cargando…</div>
        ) : (
          <div className="pc-tabla-wrap">
            <table className="pc-tabla">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Mon</th>
                  <th>Nat.</th>
                  <th style={{ textAlign: 'center' }}>Imp.</th>
                  <th style={{ textAlign: 'center' }}>Lit.</th>
                  <th style={{ textAlign: 'center' }}>Dif.</th>
                  <th>Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visibles.map(c => {
                  const tieneHijos = items.some(x => x.parent_codigo === c.codigo)
                  const expandido = expandidos.has(c.codigo)
                  const indent = (c.nivel - 1) * 14
                  const enBorrador = c.estado_validacion === 'borrador'
                  return (
                    <tr key={c.id} className={c.activa ? '' : 'pc-row-inactiva'}>
                      <td>
                        <div style={{ paddingLeft: indent, display: 'flex', alignItems: 'center', gap: 6 }}>
                          {tieneHijos && !hayFiltro ? (
                            <button className="pc-expand" onClick={() => toggleExpand(c.codigo)}>
                              {expandido ? '▼' : '▶'}
                            </button>
                          ) : <span style={{ width: 14 }} />}
                          <span className="pc-codigo" style={{ fontWeight: c.nivel <= 2 ? 900 : 700 }}>{c.codigo}</span>
                        </div>
                      </td>
                      <td style={{ fontWeight: c.nivel <= 2 ? 800 : 600, color: c.nivel <= 2 ? '#1A3461' : '#475569' }}>
                        {c.nombre}
                      </td>
                      <td>
                        <span className="pc-pill-tipo" style={{ background: COLOR_TIPO[c.tipo]?.bg, color: COLOR_TIPO[c.tipo]?.fg }}>
                          {tipoLabel(c.tipo)}
                        </span>
                      </td>
                      <td style={{ fontSize: 11, color: '#475569', fontWeight: 700 }}>{MONEDA_LABEL[c.cod_moneda ?? 0]}</td>
                      <td style={{ fontSize: 11 }}>{c.naturaleza === 'deudora' ? 'D' : c.naturaleza === 'acreedora' ? 'A' : '—'}</td>
                      <td style={{ textAlign: 'center' }}>{c.es_imputable ? '✓' : '—'}</td>
                      <td style={{ textAlign: 'center', fontSize: 11, color: '#7c3aed', fontWeight: 700 }} title={LITERALES_TRIBUTARIOS[c.literal_trib]}>
                        {c.literal_trib > 0 ? c.literal_trib : '—'}
                      </td>
                      <td style={{ textAlign: 'center', fontSize: 11, color: c.dif_cambio === 'S' ? '#dc2626' : '#94a3b8', fontWeight: 700 }}>
                        {c.dif_cambio || 'N'}
                      </td>
                      <td>
                        {enBorrador ? <span className="pc-badge-borrador">⏳ Borrador</span>
                          : c.estado_validacion === 'validado_roberto' ? <span className="pc-badge-validada">✓</span>
                          : c.estado_validacion === 'rechazado' ? <span className="pc-badge-rechazada">✗</span>
                          : '—'}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {enBorrador && (
                          <button className="pc-btn-aprobar" onClick={() => aprobarUna(c)} title="Aprobar esta cuenta">✓</button>
                        )}
                        <button className="pc-btn-edit" onClick={() => setModal(c)}>Editar</button>
                        <button className="pc-btn-del" onClick={() => borrar(c)}>×</button>
                      </td>
                    </tr>
                  )
                })}
                {visibles.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', padding: 30, color: '#94a3b8' }}>Sin resultados con esos filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="pc-info">
          <strong>📚 Plan oficial Zeta · 363 cuentas (Roberto 09-jun-2026)</strong>
          <p>
            <b>Códigos</b>: jerarquía decimal sin puntos. <code>1111101</code> = Caja Principal Pesos (Activo → Corriente → Disp → Cajas → Pesos → 01).
            Niveles 1-5 son grupos no movibles · niveles 6-7 son <i>imputables</i> (reciben asientos).
          </p>
          <p>
            <b>Literales tributarios</b> (DGI F2181): 501 PF · 502 empresas IVA básica · 503 empresas IVA mínima · 504 exentos · 505 IVA básica compras · 506 IVA mínima compras.
            Solo se setean en cuentas de IVA y ventas.
          </p>
          <p>
            <b>Dif. cambio = S</b>: la cuenta genera diferencia de cambio en el revalúo mensual (cuentas en moneda extranjera u otras determinadas por Roberto).
          </p>
        </div>
      </div>

      {modal && <ModalCuenta initial={modal} todasLasCuentas={items} onClose={() => setModal(null)} onSave={guardar} />}

      <style>{CSS}</style>
    </AdminLayout>
  )
}

function Kpi({ label, val, color }) {
  return (
    <div className="pc-kpi" style={{ borderColor: (color || '#94a3b8') + '40' }}>
      <div className="pc-kpi-label" style={{ color: color || '#475569' }}>{label}</div>
      <div className="pc-kpi-val">{val}</div>
    </div>
  )
}

function ModalCuenta({ initial, todasLasCuentas, onClose, onSave }) {
  const [f, setF] = useState({
    codigo: '',
    nombre: '',
    tipo: 'egreso',
    nivel: 6,
    parent_codigo: '',
    cuenta_externa_id: '',
    naturaleza: 'deudora',
    es_imputable: true,
    activa: true,
    cod_moneda: 1,
    literal_trib: 0,
    dif_cambio: 'N',
    notas: '',
    ...initial,
  })
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value })
  const upBool = (k) => (e) => setF({ ...f, [k]: e.target.checked })
  const upInt = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) || 0 })

  function submit(e) {
    e.preventDefault()
    if (!f.codigo.trim()) { alert('Cargá código'); return }
    if (!f.nombre.trim()) { alert('Cargá nombre'); return }
    onSave({ ...f, nivel: Number(f.nivel), parent_codigo: f.parent_codigo || null })
  }

  const posiblesParents = todasLasCuentas.filter(c => c.tipo === f.tipo && c.nivel < (Number(f.nivel) || 6) && c.codigo !== f.codigo)

  return (
    <div className="pc-modal-bg" onClick={onClose}>
      <div className="pc-modal" onClick={(e) => e.stopPropagation()}>
        <button className="pc-modal-close" onClick={onClose}>×</button>
        <h2>{f.id ? 'Editar cuenta contable' : 'Nueva cuenta contable'}</h2>

        <form onSubmit={submit}>
          <div className="pc-form-row">
            <label className="pc-field">
              <span>Código *</span>
              <input type="text" value={f.codigo} onChange={up('codigo')} placeholder="Ej: 5113051" required />
            </label>
            <label className="pc-field">
              <span>Nombre *</span>
              <input type="text" value={f.nombre} onChange={up('nombre')} placeholder="Ej: Comisiones tarjetas" required />
            </label>
          </div>

          <div className="pc-form-row">
            <label className="pc-field">
              <span>Tipo *</span>
              <select value={f.tipo} onChange={up('tipo')}>
                {TIPOS_CUENTA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
            <label className="pc-field">
              <span>Nivel</span>
              <select value={f.nivel} onChange={(e) => setF({ ...f, nivel: Number(e.target.value) })}>
                {[1,2,3,4,5,6,7].map(n => <option key={n} value={n}>Nivel {n}</option>)}
              </select>
            </label>
            <label className="pc-field">
              <span>Naturaleza</span>
              <select value={f.naturaleza || ''} onChange={up('naturaleza')}>
                <option value="">—</option>
                <option value="deudora">Deudora</option>
                <option value="acreedora">Acreedora</option>
              </select>
            </label>
          </div>

          <div className="pc-form-row">
            <label className="pc-field">
              <span>Moneda</span>
              <select value={f.cod_moneda ?? 0} onChange={upInt('cod_moneda')}>
                <option value="0">0 · Grupo (no movible)</option>
                <option value="1">1 · UYU</option>
                <option value="2">2 · USD</option>
              </select>
            </label>
            <label className="pc-field">
              <span>Literal tributario</span>
              <select value={f.literal_trib ?? 0} onChange={upInt('literal_trib')}>
                <option value="0">0 — no aplica</option>
                {Object.entries(LITERALES_TRIBUTARIOS).map(([k, v]) => (
                  <option key={k} value={k}>{k} — {v}</option>
                ))}
              </select>
            </label>
            <label className="pc-field">
              <span>Genera dif. de cambio</span>
              <select value={f.dif_cambio || 'N'} onChange={up('dif_cambio')}>
                <option value="N">N — no</option>
                <option value="S">S — sí</option>
              </select>
            </label>
          </div>

          <label className="pc-field">
            <span>Cuenta padre</span>
            <select value={f.parent_codigo || ''} onChange={up('parent_codigo')}>
              <option value="">— Sin padre (raíz) —</option>
              {posiblesParents.map(p => <option key={p.codigo} value={p.codigo}>{p.codigo} · {p.nombre}</option>)}
            </select>
          </label>

          <div className="pc-form-row">
            <label className="pc-check">
              <input type="checkbox" checked={f.es_imputable} onChange={upBool('es_imputable')} />
              <span>Es imputable</span>
            </label>
            <label className="pc-check">
              <input type="checkbox" checked={f.activa} onChange={upBool('activa')} />
              <span>Activa</span>
            </label>
          </div>

          <label className="pc-field">
            <span>Notas</span>
            <textarea rows={2} value={f.notas || ''} onChange={up('notas')} />
          </label>

          <div className="pc-form-actions">
            <button type="button" className="pc-btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="pc-btn-primary">{f.id ? 'Guardar cambios' : 'Crear cuenta'}</button>
          </div>
        </form>
      </div>
    </div>
  )
}

const CSS = `
.pc-wrap { padding: 16px 20px 32px; max-width: 1500px; margin: 0 auto }
.pc-headline { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 14px }
.pc-headline h1 { font-size: 24px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.pc-headline p { font-size: 13px; color: #64748b; margin: 0 }
.pc-headline code { background: #eef2f7; padding: 0 5px; border-radius: 4px; font-size: 11.5px }

.pc-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 8px; margin-bottom: 12px }
.pc-kpi { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 9px; padding: 8px 11px }
.pc-kpi-label { font-size: 10px; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.pc-kpi-val { font-size: 20px; font-weight: 900; color: #1A3461 }

.pc-toolbar { display: flex; gap: 7px; flex-wrap: wrap; align-items: center; margin-bottom: 10px }
.pc-toolbar input, .pc-toolbar select { padding: 6px 9px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 12.5px; font-family: inherit; color: #1A3461 }
.pc-toolbar input { min-width: 220px }
.pc-toolbar-info { margin-left: auto; font-size: 12px; color: #64748b }

.pc-banner-aprobacion {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  background: #fef3c7; border: 1px solid #fde68a; border-radius: 9px;
  padding: 10px 14px; margin-bottom: 10px; font-size: 13px; color: #78350f;
}
.pc-banner-aprobacion strong { color: #d97706 }

.pc-loading { padding: 40px; text-align: center; color: #94a3b8 }

.pc-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: auto }
.pc-tabla { width: 100%; border-collapse: collapse; font-size: 12.5px }
.pc-tabla th { text-align: left; padding: 8px 9px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 10.5px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0; position: sticky; top: 0 }
.pc-tabla td { padding: 6px 9px; border-bottom: 1px solid #f1f5f9; vertical-align: middle }
.pc-tabla tr:hover { background: #f8fafc }
.pc-row-inactiva { opacity: .5 }

.pc-expand { background: transparent; border: 0; cursor: pointer; padding: 2px 4px; font-size: 10px; color: #64748b }
.pc-expand:hover { color: #1A3461 }
.pc-codigo { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-size: 11.5px; color: #1A3461 }

.pc-pill-tipo { display: inline-block; padding: 2px 6px; border-radius: 5px; font-size: 9.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em }

.pc-badge-borrador { display: inline-block; padding: 2px 7px; border-radius: 5px; font-size: 10.5px; font-weight: 700; background: #fef3c7; color: #92400e }
.pc-badge-validada { display: inline-block; padding: 2px 8px; border-radius: 5px; font-size: 11px; font-weight: 700; background: #dcfce7; color: #166534 }
.pc-badge-rechazada { display: inline-block; padding: 2px 8px; border-radius: 5px; font-size: 11px; font-weight: 700; background: #fee2e2; color: #991b1b }

.pc-btn-aprobar { background: #16a34a; color: #fff; border: 0; padding: 3px 8px; border-radius: 5px; font-size: 11px; font-weight: 800; cursor: pointer; margin-right: 4px }
.pc-btn-aprobar:hover { background: #15803d }
.pc-btn-edit { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 3px 8px; border-radius: 5px; font-size: 10.5px; font-weight: 700; cursor: pointer; margin-right: 4px }
.pc-btn-edit:hover { background: #f1f5f9 }
.pc-btn-del { background: transparent; border: 1px solid #fecaca; color: #b91c1c; padding: 3px 7px; border-radius: 5px; font-weight: 800; cursor: pointer; font-size: 11px }
.pc-btn-del:hover { background: #fee2e2 }

.pc-btn-primary { background: #1A3461; color: #fff; border: 0; padding: 8px 16px; border-radius: 8px; font-size: 12.5px; font-weight: 800; cursor: pointer; font-family: inherit }
.pc-btn-primary:hover { background: #0f2147 }
.pc-btn-primary:disabled { background: #94a3b8; cursor: not-allowed }
.pc-btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 8px 16px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer; font-family: inherit }
.pc-btn-ghost { background: transparent; border: 1px solid #cbd5e1; color: #64748b; padding: 6px 10px; border-radius: 7px; font-size: 11.5px; font-weight: 700; cursor: pointer; font-family: inherit }
.pc-btn-ghost:hover { background: #f1f5f9; color: #1A3461 }

.pc-info { margin-top: 18px; padding: 13px 16px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12px; color: #1e3a8a; line-height: 1.55 }
.pc-info strong { font-size: 12.5px }
.pc-info b { color: #1e40af }
.pc-info code { background: #dbeafe; padding: 1px 5px; border-radius: 4px; font-size: 11px }

.pc-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px }
.pc-modal { background: #fff; border-radius: 14px; max-width: 720px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 24px 26px; position: relative }
.pc-modal h2 { margin: 0 0 16px; font-size: 19px; color: #1A3461; font-weight: 900 }
.pc-modal-close { position: absolute; top: 12px; right: 12px; background: none; border: 0; font-size: 22px; cursor: pointer; color: #64748b }

.pc-form-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 11px }
@media (max-width: 640px) { .pc-form-row { grid-template-columns: 1fr } }
.pc-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px }
.pc-field > span { font-size: 11.5px; font-weight: 700; color: #475569 }
.pc-field input, .pc-field select, .pc-field textarea { padding: 8px 10px; border: 1px solid #cbd5e1; border-radius: 7px; font-size: 13px; font-family: inherit; color: #1A3461 }
.pc-check { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #1A3461; font-weight: 700; padding: 6px 0 }
.pc-check input { width: 17px; height: 17px; accent-color: #00A8B5 }

.pc-form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 12px; padding-top: 12px; border-top: 1px solid #e2e8f0 }
`
